import React, { useState, useEffect, useRef } from "react";
import styles from "./company-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Dropdown from "../../ui/dropdown";
import Button from "../../ui/button";
import Table from "../../ui/table";
import Checkbox from "../../ui/checkbox";
import ContactItem from "../../screens/contact-item";
import EditContactModal from "../edit-contact-modal";
import FormSection from "../../screens/form-section";
import {
  getCompaniesSummary,
  getLocalities,
  getCommercials,
  createCompany,
  getCompanyById,
  editCompany,
  searchCompaniesByName,
} from "../../../services/apiService";
import { showErrorToast } from "../../../utils/notifications";

const CompanyModal = ({ isOpen, onClose, initialData }) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    locality: null,
    nif: "",
  commission: null,
    billingEmail: "",
    companyType: null,
    group: null,
    commercial: null,
    paymentDate: "",
    observations: "",
    contacts: [],
  });
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [localityOptions, setLocalityOptions] = useState([]);
  const [commercialOptions, setCommercialOptions] = useState([]);
  const [companyTypeOptions] = useState([
    { label: "S", value: "S" },
    { label: "A", value: "A" },
    { label: "B", value: "B" },
    { label: "C", value: "C" },
  ]);
  
  const [groupOptions] = useState([
    { label: "Grupo A", value: "Grupo A" },
    { label: "Grupo B", value: "Grupo B" },
  ]);
  const [isInactive, setIsInactive] = useState(false);

  const [commissionOptions] = useState([
    { label: "0%", value: 0 },
    { label: "5%", value: 5 },
    { label: "10%", value: 10 },
  ]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  
  // Estados para contactos
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState(initialData?.phoneNumber || "");
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    // Actualizar teléfono de contacto si se proporciona initialData
    if (initialData?.phoneNumber) {
      setContactPhone(initialData.phoneNumber);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch companies
        const companiesData = await getCompaniesSummary();
        
        // Formatear los datos para la tabla
        const formattedCompanies = companiesData.map(company => ({
          id: company.id,
          nombre: company.nombre,
          activo: company.activo ? "Activo" : "Inactivo",
          comision: company.comision != null ? `${company.comision}%` : "0%",
          tipoCompania: company.tipoCompania,
          grupoTrabajo: company.grupoTrabajo,
          comercial: company.comercial,
        }));

        setCompanies(formattedCompanies);

        // Fetch localities
        const localitiesData = await getLocalities();
        const formattedLocalities = localitiesData.map((locality) => ({
          label: `${locality.municipio} (${locality.codigoPostal})`,
          value: locality.id,
        }));
        setLocalityOptions(formattedLocalities);

        // Fetch commercials
        const commercialsData = await getCommercials();
        const formattedCommercials = commercialsData.map((commercial) => ({
          label: commercial.nombre,
          value: commercial.id,
        }));
        setCommercialOptions(formattedCommercials);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // No cerrar si el EditContactModal está abierto
      if (isEditContactModalOpen) {
        return;
      }
      
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isEditContactModalOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    setFormData({ ...formData, [field]: option });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddCompany = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        showErrorToast("El nombre es obligatorio");
        return;
      }
      if (formData.billingEmail && !validateEmail(formData.billingEmail)) {
        showErrorToast("El correo de facturación no es válido");
        return;
      }

      const companyData = {
        nombre: formData.name,
        activo: !isInactive,
        domicilio: formData.address,
        localidadId: formData.locality?.value,
        nif: formData.nif,
        comision: parseFloat(formData.commission?.value || formData.commission) || 0,
        correoFacturacion: formData.billingEmail,
        tipoCompania: formData.companyType?.value,
        grupoTrabajo: formData.group?.value || null, 
        comercialId: formData.commercial?.value,
        fechaPago: formData.paymentDate,
        observaciones: formData.observations,
        contactos: formData.contacts,
      };
      await createCompany(companyData);

      // Refrescar la lista de compañías
      const companiesData = await getCompaniesSummary();
      
      // Formatear los datos para la tabla
      const formattedCompanies = companiesData.map(company => ({
        id: company.id,
        nombre: company.nombre,
        activo: company.activo ? "Activo" : "Inactivo",
        comision: company.comision != null ? `${company.comision}%` : "0%",
        tipoCompania: company.tipoCompania,
        grupoTrabajo: company.grupoTrabajo,
        comercial: company.comercial,
      }));
      
      setCompanies(formattedCompanies);

      // Limpiar el formulario
      setFormData({
        name: "",
        address: "",
        locality: null,
        nif: "",
        commission: null,
        billingEmail: "",
        companyType: null,
        group: null,
        commercial: null,
        paymentDate: "",
        observations: "",
        contacts: [],
      });
      setIsInactive(false);
      setContactName("");
      setContactPhone("");

    } catch (error) {
      console.error("Error al crear compañía:", error);
    }
  };

  const handleEditCompany = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        showErrorToast("El nombre es obligatorio");
        return;
      }
      if (formData.billingEmail && !validateEmail(formData.billingEmail)) {
        showErrorToast("El correo de facturación no es válido");
        return;
      }

      const companyData = {
        id: editingCompanyId,
        nombre: formData.name,
        activo: !isInactive,
        domicilio: formData.address,
        localidadId: formData.locality?.value,
        nif: formData.nif,
        comision: parseFloat(formData.commission?.value || formData.commission) || 0,
        correoFacturacion: formData.billingEmail,
        tipoCompania: formData.companyType?.value,
        grupoTrabajo: formData.group?.value || null,
        comercialId: formData.commercial?.value,
        fechaPago: formData.paymentDate,
        observaciones: formData.observations,
        contactos: formData.contacts,
      };

      await editCompany(editingCompanyId, companyData);


      // Refrescar la lista de compañías
      const companiesData = await getCompaniesSummary();
      
      // Formatear los datos para la tabla
      const formattedCompanies = companiesData.map(company => ({
        id: company.id,
        nombre: company.nombre,
        activo: company.activo ? "Activo" : "Inactivo",
        comision: company.comision != null ? `${company.comision}%` : "0%",
        tipoCompania: company.tipoCompania,
        grupoTrabajo: company.grupoTrabajo,
        comercial: company.comercial,
      }));
      
      setCompanies(formattedCompanies);

      // Limpiar el formulario y salir del modo edición
      setFormData({
        name: "",
        address: "",
        locality: null,
        nif: "",
        commission: null,
        billingEmail: "",
        companyType: null,
        group: null,
        commercial: null,
        paymentDate: "",
        observations: "",
      });
      setIsInactive(false);
      setIsEditMode(false);
      setEditingCompanyId(null);
    } catch (error) {
      console.error("Error al editar compañía:", error);
    }
  };

  const handleCompanyRowClick = async (company) => {
    try {
      const companyDetails = await getCompanyById(company.id);

      console.log("Datos de la compañía desde API:", companyDetails);

      // Buscar la localidad en las opciones
      const localityOption = localityOptions.find(
        (opt) => opt.value === companyDetails.localidadId
      );

      // Buscar el tipo de compañía en las opciones
      const companyTypeOption = companyTypeOptions.find(
        (opt) => opt.value === companyDetails.tipoCompania
      );

      // Buscar el grupo en las opciones
      const groupOption = groupOptions.find(
        (opt) => opt.value === companyDetails.grupoTrabajo
      );

      // Buscar el comercial en las opciones
      const commercialOption = commercialOptions.find(
        (opt) => opt.value === companyDetails.comercialId
      );
      
      console.log("Grupo encontrado:", groupOption, "de grupoTrabajo:", companyDetails.grupoTrabajo);

      // Llenar el formulario con los datos de la compañía
      setFormData({
        name: companyDetails.nombre || "",
        address: companyDetails.domicilio || "",
        locality: localityOption || null,
        nif: companyDetails.nif || "",
  commission: companyDetails.comision != null ? { label: `${companyDetails.comision}%`, value: String(companyDetails.comision) } : null,
        billingEmail: companyDetails.correoFacturacion || "",
        companyType: companyTypeOption || null,
        group: groupOption || null,
        commercial: commercialOption || null,
        paymentDate: companyDetails.fechaPago || "",
        observations: companyDetails.observaciones || "",
        contacts: companyDetails.contactos || [],
      });

      setIsInactive(!companyDetails.activo);
      setIsEditMode(true);
      setEditingCompanyId(company.id);
      
      // Hacer scroll al inicio del modal y focus en el primer input
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error al cargar los detalles de la compañía:", error);
    }
  };
  
  // Funciones de manejo de contactos
  const handleAddContact = () => {
    if (!contactName.trim() || !contactPhone.trim()) {
      alert("Por favor completa el nombre y teléfono del contacto");
      return;
    }

    const newContact = {
      nombre: contactName.trim(),
      telefono: contactPhone.trim(),
    };

    setFormData({
      ...formData,
      contacts: [...formData.contacts, newContact],
    });

    setContactName("");
    setContactPhone("");
  };

  const handleRemoveContact = (contactId) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, index) => index !== contactId),
    });
  };

  const handleEditContact = (contact, index) => {
    setEditingContact({ ...contact, index });
    setIsEditContactModalOpen(true);
  };

  const handleContactClick = (contact, index) => {
    handleEditContact(contact, index);
  };

  const handleSaveContact = (editedContact) => {
    const updatedContacts = [...formData.contacts];
    updatedContacts[editingContact.index] = {
      nombre: editedContact.name,
      telefono: editedContact.phone,
    };

    setFormData({
      ...formData,
      contacts: updatedContacts,
    });

    setIsEditContactModalOpen(false);
    setEditingContact(null);
  };

  

  const handleCancelEdit = () => {
    setFormData({
      name: "",
      address: "",
      locality: null,
      nif: "",
  commission: null,
      billingEmail: "",
      companyType: null,
      group: null,
      commercial: null,
      paymentDate: "",
      observations: "",
      contacts: [],
    });
    setIsInactive(false);
    setIsEditMode(false);
    setEditingCompanyId(null);
    setContactName("");
    setContactPhone("");
  };

  const handleSearchCompanies = async (value) => {
    setSearchTerm(value);
    
    if (!value || value.trim() === "") {
      // Si no hay término de búsqueda, cargar todas las compañías
      try {
        const companiesData = await getCompaniesSummary();
        const formattedCompanies = companiesData.map(company => ({
          id: company.id,
          nombre: company.nombre,
          activo: company.activo ? "Activo" : "Inactivo",
          comision: company.comision != null ? `${company.comision}%` : "0%",
          tipoCompania: company.tipoCompania,
          grupoTrabajo: company.grupoTrabajo,
          comercial: company.comercial,
        }));
        setCompanies(formattedCompanies);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
      return;
    }

    // Buscar compañías por nombre
    try {
      const searchResults = await searchCompaniesByName(value);
      const formattedCompanies = searchResults.map(company => ({
        id: company.id,
        nombre: company.nombre,
        activo: company.activo ? "Activo" : "Inactivo",
        comision: company.comision != null ? `${company.comision}%` : "0%",
        tipoCompania: company.tipoCompania,
        grupoTrabajo: company.grupoTrabajo,
        comercial: company.comercial,
      }));
      setCompanies(formattedCompanies);
    } catch (error) {
      console.error("Error searching companies:", error);
    }
  };

  const headers = [
    { label: "Nombre", key: "nombre" },
    { label: "Activo", key: "activo" },
    { label: "Comisión", key: "comision" },
    { label: "Tipo", key: "tipoCompania" },
    { label: "Grupo", key: "grupoTrabajo" },
    { label: "Comercial", key: "comercial" },
  ];

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Compañías</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons name="close" width={24} height={24} color="black" />
          </button>
        </div>

        <FormSection title="Datos de la Compañía" style={{ marginBottom: "12px" }}>
          {/* Primera fila: Nombre, Domicilio, Localidad, NIF */}
          <div className={styles.sectionRow}>
            <Input
              ref={firstInputRef}
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Domicilio"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label="Localidad"
              options={localityOptions}
              onSelect={(option) => handleDropdownSelect("locality", option)}
              selected={formData.locality}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
            />
            <Input
              label="NIF"
              name="nif"
              value={formData.nif}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
          </div>

          {/* Segunda fila: Comisión, Correo facturación, Tipo de compañía, Grupo */}
          <div className={styles.sectionRow}>
            <Dropdown
              label="Comisión"
              options={commissionOptions}
              onSelect={(option) => handleDropdownSelect("commission", option)}
              selected={formData.commission}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
            />
            <Input
              label="Correo facturación"
              name="billingEmail"
              value={formData.billingEmail}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label="Tipo de compañía"
              options={companyTypeOptions}
              onSelect={(option) => handleDropdownSelect("companyType", option)}
              selected={formData.companyType}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
            />
            <Dropdown
              label="Grupo"
              options={groupOptions}
              onSelect={(option) => handleDropdownSelect("group", option)}
              selected={formData.group}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
            />
          </div>

          {/* Tercera fila: Comercial, Fecha de pago, Observaciones */}
          <div className={styles.sectionRow}>
            <Dropdown
              label="Comercial"
              options={commercialOptions}
              onSelect={(option) => handleDropdownSelect("commercial", option)}
              selected={formData.commercial}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
            />
            <Input
              label="Fecha de pago"
              name="paymentDate"
              type="text"
              value={formData.paymentDate}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Observaciones"
              name="observations"
              value={formData.observations}
              onChange={handleInputChange}
              style={{ flex: 2 }}
            />
          </div>
          
          <Checkbox
            label="Compañía inactiva"
            checked={isInactive}
            onChange={(checked) => setIsInactive(checked)}
          />
        </FormSection>
          
        {/* Sección de Contactos */}
        <FormSection title="Contactos" style={{ marginBottom: "12px" }}>
            <div className={styles.sectionRow}>
              <Input
                label="Nombre"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Nombre del contacto"
                style={{ flex: 1 }}
              />
              <Input
                label="Teléfono"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Teléfono"
                style={{ flex: 1 }}
              />
            </div>
            
            {/* Lista de contactos */}
            {formData.contacts && formData.contacts.length > 0 && (
              <div className={styles.contactsList}>
                {formData.contacts.map((contact, index) => (
                  <ContactItem
                    key={index}
                    contact={{
                      id: index,
                      name: contact.nombre,
                      phone: contact.telefono,
                      address: "",
                    }}
                    onRemove={handleRemoveContact}
                    onClick={() => handleContactClick(contact, index)}
                    onEdit={() => handleEditContact(contact, index)}
                  />
                ))}
              </div>
            )}
            
            <Button 
              type="button" 
              variant="secondary"
              onClick={handleAddContact}
              style={{ marginTop: "24px" }}
            >
              AÑADIR CONTACTO
            </Button>
          </FormSection>

        <div style={{ marginTop: "12px", marginBottom: "24px", display: "flex", gap: "16px" }}>
          {isEditMode ? (
            <>
              <Button type="button" variant="primary" onClick={handleEditCompany}>
                EDITAR COMPAÑÍA
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                CANCELAR
              </Button>
            </>
          ) : (
            <Button type="button" variant="primary" onClick={handleAddCompany}>
              AÑADIR COMPAÑÍA
            </Button>
          )}
        </div>

        <div className={styles.tableSection}>
          <Input
            label="Buscar compañía"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => handleSearchCompanies(e.target.value)}
            style={{ marginBottom: "16px" }}
          />
          <Table 
            headers={headers} 
            data={companies} 
            onPress={handleCompanyRowClick}
          />
        </div>
      </div>
      
      <EditContactModal
        isOpen={isEditContactModalOpen}
        onClose={() => {
          setIsEditContactModalOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact ? {
          ...editingContact,
          name: editingContact.nombre,
          phone: editingContact.telefono,
        } : null}
        onSave={handleSaveContact}
        rolOptions={[]}
        simpleMode={true}
      />
    </div>
  );
};

export default CompanyModal;
